import { useState } from "react";
import { SelectionDropdown } from "../../../../../../../components/Dropdown/SelectionDropdown.tsx";
import { ClickableIcon } from "../../../../../../../components/Icon/ClickableIcon.tsx";
import { PanelSection } from "../../../../../components/Panel/PanelSection.tsx";
import { useLayers } from "../../../Designer/hooks/useLayers.ts";
import { designerCanvasStore } from "../../../Designer/stores/designerCanvasStore.ts";
import type {
  AlignmentAnchor,
  AlignmentScope,
} from "../../../Designer/types.ts";
import {
  alignLayersRelativeToCanvas,
  alignLayersRelativeToSelection,
} from "../../../Designer/utils/layer.ts";
import {
  LAYER_ALIGNMENT_ANCHOR_BUTTONS,
  LAYER_ALIGNMENT_SCOPE_BUTTONS,
} from "./display.ts";

export const LayersAlignmentSection = () => {
  const [alignmentScope, setAlignmentScope] =
    useState<AlignmentScope>("canvas");

  return (
    <PanelSection title="Align">
      <div className="flex-col gap-300">
        <div className="flex-row items-center gap-200">
          <span className="label-md-default text-secondary">To</span>
          <SelectionDropdown
            value={alignmentScope}
            onChange={(value) => setAlignmentScope(value as AlignmentScope)}
            options={Object.entries(LAYER_ALIGNMENT_SCOPE_BUTTONS).map(
              ([key, button]) => ({
                value: key,
                label: button.label,
                iconName: button.iconName,
              }),
            )}
            size="md"
            buttonClassName="w-[110px] pl-200"
          />
        </div>
        <div className="flex-row items-center gap-200">
          <span className="label-md-default text-secondary">Horizontal</span>
          {["left", "centerX", "right"].map((position) => (
            <AlignmentButton
              key={position}
              anchor={position as AlignmentAnchor}
              scope={alignmentScope}
            />
          ))}
        </div>
        <div className="flex-row items-center gap-200">
          <span className="label-md-default text-secondary">Vertical</span>
          {["top", "centerY", "bottom"].map((position) => (
            <AlignmentButton
              key={position}
              anchor={position as AlignmentAnchor}
              scope={alignmentScope}
            />
          ))}
        </div>
      </div>
    </PanelSection>
  );
};

const AlignmentButton = ({
  anchor,
  scope,
}: {
  anchor: AlignmentAnchor;
  scope: AlignmentScope;
}) => {
  const { fabricCanvas, selectedLayerIds } = designerCanvasStore.useState(
    (it) => ({
      fabricCanvas: it.fabricCanvas,
      selectedLayerIds: it.selectedLayerIds,
    }),
  );
  const selectedLayers = useLayers().filter((it) =>
    selectedLayerIds.includes(it.id),
  );
  const alignLayers = () => {
    if (!fabricCanvas) return;
    switch (scope) {
      case "canvas":
        alignLayersRelativeToCanvas(fabricCanvas, selectedLayers, anchor);
        break;
      case "selection":
        alignLayersRelativeToSelection(fabricCanvas, selectedLayers, anchor);
        break;
    }
  };

  // FIXME:Currently, the alignment buttons are always enabled and clickable, regardless of whether the selected layers
  //  are already aligned in that particular direction. We should add visual feedback to indicate when the layers are already aligned.
  //  However this represents a non-trivial feature because it requires to add event listeners to track layer position changes which is
  //  not easy to implement due to:
  //  - Real-time state computation
  //  - Performance optimization (debouncing/throttling updates)
  //  - Handling precision issues with floating-point coordinates
  return (
    <ClickableIcon
      name={LAYER_ALIGNMENT_ANCHOR_BUTTONS[anchor].iconName}
      variant="secondary"
      onClick={alignLayers}
      tooltip={{
        content: LAYER_ALIGNMENT_ANCHOR_BUTTONS[anchor].tooltip,
      }}
    />
  );
};
